/**
 * Flattens an object.
 * Source: https://www.basedash.com/blog/how-to-flatten-an-object-in-javascript
 *
 * @param obj JS object.
 * @param parentKey parent key to begin flattening.
 * @returns flattened object.
 */
export function flattenObject(obj: object, parentKey = '') {
  const flattened = {} as object

  for (const key in obj) {
    const k = key as keyof object
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const propName = parentKey ? `${parentKey}.${key}` : key

      if (typeof obj[k] === 'object' && obj[k] !== null && !Array.isArray(obj[k])) {
        Object.assign(flattened, flattenObject(obj[k], propName))
      } else {
        flattened[propName as keyof object] = obj[k]
      }
    }
  }

  return flattened
}

/**
 * Gets the computed value from a CSS variable.
 * @param cssVar The CSS variable name (with or without var())
 * @returns The hex color value
 * refs: https://developer.mozilla.org/en-US/docs/Web/API/Window/getComputedStyle
 */
export const getCssVarValue = (cssVar: string): string => {
  // If the input is already wrapped in var(), extract the variable name
  const varName = cssVar.startsWith('var(') ? cssVar.slice(4, -1) : cssVar
  // Get the computed value
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
}

/**
 * Converts a hex color to an rgba color.
 * @param hex The hex color.
 * @param alpha The alpha value.
 * @returns The rgba color.
 */
export const hex2rgba = (hex: string, alpha: number): string => {
  const [r, g, b] = hex.match(/\w\w/g)!.map((x) => parseInt(x, 16))
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Downloads a file by creating and virtually clicking a dynamically-rendered `a` tag
 * @param file The file to download.
 * @param filename The filename to attribute to it.
 */
export const downloadFile = (file: Blob, filename: string) => {
  const url = URL.createObjectURL(file)
  downloadLinkTarget(url, filename)

  // Clean up the URL object after download starts
  URL.revokeObjectURL(url)
}

/**
 * Downloads a url's target by creating and virtually clicking a dynamically-rendered `a` tag
 * @param file The file to download.
 * @param filename The filename to attribute to it.
 */
export const downloadLinkTarget = (url: string, filename: string) => {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.target = '_blank'
  anchor.download = filename
  anchor.click()
}

// Get CSS custom property for cost color coding
export const getCostColor = (cost: number | null | undefined) => {
  if (cost == null) return ''
  if (cost > 0) return 'var(--text-badge-emerald)'
  if (cost < 0) return 'var(--text-badge-orange)'
  return ''
}
